import { useEffect, useRef, useState } from 'react';
import {
    ThemeProvider,
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    MenuItem,
    Stack,
    TextField,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
} from '@mui/material';
import tableThemeCustom from '../../form/tableThemeCustom';
import * as EmpresaAPI from '../../../api/empresa';
import * as API from '../../../api/unidadeCondominial';
import { pessoaById } from '../../../api/pessoa';
import { getEmpresaScope } from '../../../helpers/sessionEmpresa';
import { useNavigateParams } from '../../../hooks/useNavigateParams';

const formatarCompetencia = (referencia) => {
    const meses = [
        'Janeiro',
        'Fevereiro',
        'Março',
        'Abril',
        'Maio',
        'Junho',
        'Julho',
        'Agosto',
        'Setembro',
        'Outubro',
        'Novembro',
        'Dezembro',
    ];
    const [ano, mes] = String(referencia || '').split('-');
    return meses[Number(mes) - 1] ? `${meses[Number(mes) - 1]} de ${ano}` : referencia;
};

const labels = {
    apta: 'Apta na prévia',
    provisionada: 'Provisionada',
    bloqueada: 'Bloqueada',
    sem_servicos: 'Sem serviços',
    gerada: 'Já registrada',
    pendente: 'Gerada com pendência',
};
const cores = {
    apta: 'success',
    provisionada: 'warning',
    bloqueada: 'error',
    sem_servicos: 'default',
    gerada: 'info',
    pendente: 'warning',
};
const moeda = (v) => (v == null ? '—' : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }));
const dataBR = (v) => (v ? v.split('-').reverse().join('/') : 'Não definido');
const proximoMes = () => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() + 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

export default function CobrancaPrevia() {
    const navigate = useNavigateParams();
    const theme = tableThemeCustom();
    const [empresaId, setEmpresaId] = useState(() => String(getEmpresaScope().empresaId || ''));
    const [empresas, setEmpresas] = useState([]);
    const [operacoes, setOperacoes] = useState([]);
    const [operacaoId, setOperacaoId] = useState('');
    const [mes, setMes] = useState(proximoMes);
    const [resultado, setResultado] = useState(null);
    const [executando, setExecutando] = useState(false);
    const [historico, setHistorico] = useState([]);
    const [regularizacao, setRegularizacao] = useState(null);
    const [aviso, setAviso] = useState('');
    const [erro, setErro] = useState('');
    const [carregando, setCarregando] = useState(false);
    const [buscandoOperacoes, setBuscandoOperacoes] = useState(false);
    const [filtro, setFiltro] = useState('todos');
    const [busca, setBusca] = useState('');
    const requisicao = useRef(0);
    useEffect(() => {
        let ativo = true;
        EmpresaAPI.empresa()
            .then((r) => {
                if (ativo) setEmpresas(Array.isArray(r) ? r : r?.data || []);
            })
            .catch(() => {
                if (ativo) setErro('Não foi possível carregar as empresas.');
            });
        return () => {
            ativo = false;
            requisicao.current++;
        };
    }, []);
    useEffect(() => {
        let ativo = true;
        setOperacoes([]);
        setOperacaoId('');
        setBuscandoOperacoes(false);
        if (!empresaId) return undefined;
        setBuscandoOperacoes(true);
        API.operacoesCobranca(empresaId)
            .then(({ data }) => {
                if (!ativo) return;
                setOperacoes(data);
                if (data.length === 1) setOperacaoId(String(data[0].id));
            })
            .catch((e) => {
                if (ativo) setErro(e.response?.data?.message || 'Não foi possível carregar as operações.');
            })
            .finally(() => {
                if (ativo) setBuscandoOperacoes(false);
            });
        return () => {
            ativo = false;
        };
    }, [empresaId]);
    const [operadores, setOperadores] = useState({});

    useEffect(() => {
        let ativo = true;

        const ids = [...new Set(historico.map((execucao) => execucao.createdUser).filter(Boolean))];

        async function carregarOperadores() {
            const pessoas = await Promise.all(
                ids.map(async (id) => {
                    try {
                        const resposta = await pessoaById(id);
                        return [id, resposta?.data?.descricao || `Operador ${id}`];
                    } catch {
                        return [id, `Operador ${id}`];
                    }
                })
            );

            if (ativo) {
                setOperadores(Object.fromEntries(pessoas));
            }
        }

        carregarOperadores();

        return () => {
            ativo = false;
        };
    }, [historico]);
    const invalidar = () => {
        requisicao.current++;
        setResultado(null);
        setErro('');
        setCarregando(false);
        setHistorico([]);
        setRegularizacao(null);
        setAviso('');
    };
    const parametros = () => {
        const [ano, numeroMes] = mes.split('-');
        return { empresaId, operacaoId, ano, mes: numeroMes };
    };
    async function carregarHistorico() {
        const id = requisicao.current;
        try {
            const { data } = await API.historicoCobranca(parametros());
            if (id === requisicao.current) setHistorico(data);
        } catch (e) {
            if (id === requisicao.current) setErro(e.response?.data?.message || 'Não foi possível carregar o histórico.');
        }
    }
    async function gerar() {
        if (!resultado || executando) return;
        if (!window.confirm(`Gerar as cobranças de ${mes} conforme esta prévia? Cobranças com pendências ficarão provisionadas.`))
            return;
        setExecutando(true);
        setErro('');
        setAviso('');
        try {
            const { data } = await API.gerarCobranca({
                ...parametros(),
                unidades: resultado.data.map((r) => ({ unidadeId: r.unidadeId, assinatura: r.assinatura })),
            });
            setAviso(`Execução ${data.execucaoId} concluída. Confira os resultados e pendências no histórico abaixo.`);
        } catch (e) {
            setErro(
                e.response?.data?.message ||
                    'A conexão foi interrompida. Consulte o histórico antes de repetir; algumas unidades podem ter sido gravadas.'
            );
        } finally {
            setResultado(null);
            await carregarHistorico();
            setExecutando(false);
        }
    }
    async function regularizar(e) {
        e.preventDefault();
        setExecutando(true);
        setErro('');
        try {
            const { data } = await API.regularizarCobranca({ ...regularizacao, empresaId });
            setAviso(data.message);
            setRegularizacao(null);
            setResultado(null);
            await carregarHistorico();
        } catch (error) {
            setErro(error.response?.data?.message || 'Não foi possível regularizar. Atualize a prévia para conferir a situação.');
        } finally {
            setExecutando(false);
        }
    }
    async function consultar(e) {
        e.preventDefault();
        if (executando) return;
        if (!empresaId || !operacaoId || !/^\d{4}-\d{2}$/.test(mes)) {
            setErro('Informe empresa, operação e mês/ano.');
            return;
        }
        const id = ++requisicao.current;
        setCarregando(true);
        setResultado(null);
        setErro('');
        try {
            const [ano, numeroMes] = mes.split('-');
            const { data } = await API.previaCobranca({ empresaId, operacaoId, ano, mes: numeroMes });
            if (id === requisicao.current) {
                setResultado(data);
                setFiltro('todos');
            }
        } catch (error) {
            if (id === requisicao.current) setErro(error.response?.data?.message || 'Não foi possível consultar a prévia.');
        } finally {
            if (id === requisicao.current) setCarregando(false);
        }
    }
    const linhas = (resultado?.data || []).filter(
        (r) =>
            (filtro === 'todos' || r.status === filtro) &&
            `${r.unidade} ${r.descricao} ${r.responsavel?.descricao || ''}`
                .toLocaleLowerCase('pt-BR')
                .includes(busca.toLocaleLowerCase('pt-BR'))
    );
    return (
        <ThemeProvider theme={theme}>
            <Box
                sx={{
                    p: { xs: 1.5, md: 3 },
                    bgcolor: 'background.default',
                    color: 'text.primary',
                    borderRadius: 2,
                    minWidth: 0,
                    '& .MuiTypography-root': { color: 'inherit' },
                    '& .MuiFormHelperText-root': { color: 'text.secondary', fontSize: '0.8rem' },
                    '& .MuiTableCell-root': { color: 'text.primary' },
                    '& .MuiTableCell-head': { bgcolor: 'action.hover', fontWeight: 700 },
                }}
            >
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                    <Button onClick={() => navigate('/UnidadesCondominiais', new URLSearchParams(window.location.search))}>
                        Voltar
                    </Button>
                    <Typography variant="h5">Cobrança condominial</Typography>
                </Stack>
                <Alert severity="info" sx={{ mb: 2 }}>
                    Confira as cobranças previstas para o mês. Esta consulta não grava movimentos nem lançamentos.
                </Alert>
                <Paper
                    component="form"
                    variant="outlined"
                    onSubmit={consultar}
                    sx={{ p: { xs: 2, md: 2.5 }, bgcolor: 'background.paper', color: 'text.primary', borderRadius: 2 }}
                >
                    <Stack
                        component="fieldset"
                        disabled={executando}
                        sx={{ border: 0, p: 0, m: 0 }}
                        direction="row"
                        useFlexGap
                        flexWrap="wrap"
                        spacing={2}
                    >
                        <TextField
                            select
                            label="Empresa"
                            value={empresaId}
                            required
                            sx={{ width: { xs: '100%', sm: 280 } }}
                            onChange={(e) => {
                                invalidar();
                                setEmpresaId(e.target.value);
                            }}
                        >
                            {empresas.map((p) => (
                                <MenuItem key={p.id} value={String(p.id)}>
                                    {p.nomeFantasia || p.descricao || p.label || p.id}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select
                            label="Operação"
                            value={operacaoId}
                            required
                            disabled={buscandoOperacoes}
                            sx={{ width: { xs: '100%', sm: 280 } }}
                            onChange={(e) => {
                                invalidar();
                                setOperacaoId(e.target.value);
                            }}
                            helperText="Serviços configurados para contas a receber"
                        >
                            {operacoes.map((p) => (
                                <MenuItem key={p.id} value={String(p.id)}>
                                    {p.descricao}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label="Mês da cobrança"
                            type="month"
                            value={mes}
                            required
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ min: '1900-01', max: '9998-12' }}
                            onChange={(e) => {
                                invalidar();
                                setMes(e.target.value);
                            }}
                            helperText="Mês do vencimento"
                        />
                        <Button
                            sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' }, minHeight: 56 }}
                            variant="contained"
                            type="submit"
                            disabled={executando || carregando || buscandoOperacoes || !operacaoId}
                        >
                            {carregando ? <CircularProgress size={22} /> : 'Consultar prévia'}
                        </Button>
                    </Stack>
                </Paper>
                {aviso && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                        {aviso}
                    </Alert>
                )}
                <Button disabled={executando || !empresaId || !mes} onClick={carregarHistorico} sx={{ mt: 2 }}>
                    Consultar histórico e pendências do mês
                </Button>
                {executando && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                        Processando. Aguarde a conclusão; não feche esta tela.
                    </Alert>
                )}
                {historico.map((execucao) => (
                    <Paper key={execucao.id} variant="outlined" sx={{ p: 2, my: 2 }}>
                        <details>
                            <summary>
                                Execução {execucao.id}
                                {' — '}
                                {formatarCompetencia(execucao.referencia)}
                                {' — '}
                                {execucao.status === 'concluida' ? 'Concluída' : 'Em processamento'}
                                {' — Gerado por: '}
                                {operadores[execucao.createdUser] || `Usuário ${execucao.createdUser}`}
                            </summary>
                            {execucao.status === 'processando' && (
                                <Alert severity="warning">
                                    Execução sem conclusão registrada. Atualize a prévia para conferir as cobranças já gravadas
                                    antes de repetir.
                                </Alert>
                            )}
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Unidade</TableCell>
                                            <TableCell>Resultado</TableCell>
                                            <TableCell>Movimento</TableCell>
                                            <TableCell>Pendência / orientação</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(execucao.resultado || []).map((r) => (
                                            <TableRow key={r.unidadeId}>
                                                <TableCell>
                                                    {r.unidade || r.unidadeId}
                                                    <Typography variant="caption" display="block">
                                                        {r.responsavel?.descricao}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>{r.status}</TableCell>
                                                <TableCell>{r.movimentoId || '—'}</TableCell>
                                                <TableCell>
                                                    {r.mensagem}
                                                    {r.criticas?.map((c, i) => (
                                                        <Typography key={i} variant="body2">
                                                            {c.mensagem} Corrigir em: {c.local}.
                                                        </Typography>
                                                    ))}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </details>
                    </Paper>
                ))}
                {regularizacao && (
                    <Paper component="form" onSubmit={regularizar} variant="outlined" sx={{ p: 2, my: 2 }}>
                        <Typography variant="h6">Regularizar cobrança {regularizacao.cobrancaId}</Typography>
                        <Typography sx={{ mb: 2 }}>
                            Informe a data acordada e o motivo. A confirmação retira a provisão do movimento e do lançamento,
                            preservando valores e condições originais.
                        </Typography>
                        <Stack spacing={2}>
                            <TextField
                                type="date"
                                label="Novo vencimento"
                                required
                                InputLabelProps={{ shrink: true }}
                                value={regularizacao.dataVencimento}
                                onChange={(e) => setRegularizacao({ ...regularizacao, dataVencimento: e.target.value })}
                            />
                            <TextField
                                label="Motivo da regularização"
                                required
                                multiline
                                inputProps={{ minLength: 5, maxLength: 2000 }}
                                value={regularizacao.motivo}
                                onChange={(e) => setRegularizacao({ ...regularizacao, motivo: e.target.value })}
                            />
                            <Button type="submit" variant="contained" disabled={executando}>
                                Confirmar regularização e retirar provisão
                            </Button>
                            <Button disabled={executando} onClick={() => setRegularizacao(null)}>
                                Cancelar
                            </Button>
                        </Stack>
                    </Paper>
                )}
                {erro && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {erro}
                    </Alert>
                )}
                {!buscandoOperacoes && empresaId && !operacoes.length && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        Nenhuma operação de serviço para receber foi localizada. Confira os parâmetros da operação.
                    </Alert>
                )}
                {resultado && (
                    <>
                        <Paper
                            variant="outlined"
                            sx={{ my: 2, p: 2, bgcolor: 'background.paper', color: 'text.primary', borderRadius: 2 }}
                        >
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1.5, sm: 5 }}>
                                <Box>
                                    <Typography variant="body2">Vínculos vigentes em</Typography>
                                    <Typography fontWeight={700}>{dataBR(resultado.dataVigenciaVinculos)}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="body2">Preços consultados em</Typography>
                                    <Typography fontWeight={700}>{dataBR(resultado.dataConsulta)}</Typography>
                                </Box>
                            </Stack>
                        </Paper>
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            {Object.keys(labels).map((k) => (
                                <Chip
                                    key={k}
                                    variant={filtro === k ? 'filled' : 'outlined'}
                                    aria-pressed={filtro === k}
                                    color={cores[k]}
                                    label={`${labels[k]}: ${resultado.resumo[k]}`}
                                    onClick={() => setFiltro(filtro === k ? 'todos' : k)}
                                />
                            ))}
                            <Chip
                                sx={{ fontWeight: 700, color: 'text.primary', bgcolor: 'action.selected' }}
                                label={`Total aptas + provisionadas: ${moeda(resultado.resumo.total)}`}
                            />
                        </Stack>
                        <Paper
                            variant="outlined"
                            sx={{
                                my: 2,
                                p: { xs: 2, md: 2.5 },
                                bgcolor: 'background.paper',
                                color: 'text.primary',
                                borderRadius: 2,
                            }}
                        >
                            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
                                Condições da operação
                            </Typography>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
                                    gap: 2,
                                }}
                            >
                                {[
                                    [
                                        'Desconto por pontualidade',
                                        `${resultado.condicoes.cobrancaDescontoPontualidadePercentual ?? '—'}%`,
                                        'Até o vencimento',
                                    ],
                                    [
                                        'Multa por atraso',
                                        `${resultado.condicoes.cobrancaMultaPercentual ?? '—'}%`,
                                        'Aplicação única',
                                    ],
                                    [
                                        'Juros ao mês',
                                        `${resultado.condicoes.cobrancaJurosMensalPercentual ?? '—'}%`,
                                        'Proporcionais aos dias de atraso',
                                    ],
                                    [
                                        'Prazo após vencimento',
                                        resultado.condicoes.cobrancaLimiteRecebimentoDias == null
                                            ? 'Não definido'
                                            : `${resultado.condicoes.cobrancaLimiteRecebimentoDias} dias`,
                                        'Limite para recebimento',
                                    ],
                                ].map(([titulo, valor, detalhe]) => (
                                    <Box key={titulo} sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1.5 }}>
                                        <Typography variant="body2">{titulo}</Typography>
                                        <Typography variant="h6" fontWeight={700} sx={{ my: 0.5 }}>
                                            {valor}
                                        </Typography>
                                        <Typography variant="body2">{detalhe}</Typography>
                                    </Box>
                                ))}
                            </Box>
                            <Typography variant="body2" sx={{ mt: 2, lineHeight: 1.6 }}>
                                Os valores da prévia representam o principal, sem desconto por pontualidade ou encargos futuros.
                            </Typography>
                        </Paper>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Cobranças mensais já registradas não serão duplicadas, inclusive as provisionadas ou canceladas. Os
                            dados serão conferidos novamente antes da gravação.
                        </Alert>
                        <Button
                            variant="contained"
                            disabled={
                                executando ||
                                !resultado.data.some((r) => ['apta', 'provisionada', 'bloqueada'].includes(r.status))
                            }
                            onClick={gerar}
                        >
                            Gerar lote e registrar pendências
                        </Button>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ my: 2 }}>
                            <TextField
                                size="small"
                                sx={{ flex: 1 }}
                                label="Buscar unidade ou responsável"
                                value={busca}
                                onChange={(e) => setBusca(e.target.value)}
                            />
                            <Button
                                onClick={() => {
                                    setFiltro('todos');
                                    setBusca('');
                                }}
                            >
                                Mostrar todas
                            </Button>
                        </Stack>
                        <Stack spacing={2}>
                            {linhas.map((r) => (
                                <Paper
                                    key={r.unidadeId}
                                    variant="outlined"
                                    sx={{
                                        p: { xs: 1.5, md: 2.5 },
                                        bgcolor: 'background.paper',
                                        color: 'text.primary',
                                        borderRadius: 2,
                                    }}
                                >
                                    <Stack direction="row" useFlexGap flexWrap="wrap" spacing={2} justifyContent="space-between">
                                        <Typography variant="h6">
                                            {r.unidade} — {r.descricao}
                                        </Typography>
                                        <Chip size="small" color={cores[r.status]} label={labels[r.status]} />
                                    </Stack>
                                    <Typography sx={{ mt: 1, lineHeight: 1.7 }}>
                                        Responsável: {r.responsavel?.descricao || 'Não identificado'} · Vencimento:{' '}
                                        {dataBR(r.vencimento)} {r.origemVencimento ? `(${r.origemVencimento})` : ''}
                                    </Typography>
                                    {r.financeiro && (
                                        <Typography variant="body2" sx={{ mt: 0.5, mb: 1.5, lineHeight: 1.7 }}>
                                            Conta: {r.financeiro.contaIdDescricao || 'Não definida'} ({r.financeiro.contaIdOrigem}
                                            ) · Forma: {r.financeiro.formaIdDescricao || 'Não definida'} · Prazo:{' '}
                                            {r.financeiro.prazoIdDescricao || 'Não definido'} · Centro de custo:{' '}
                                            {r.financeiro.centroCustoIdDescricao || 'Não definido'}
                                        </Typography>
                                    )}
                                    {!!r.itens.length && (
                                        <TableContainer>
                                            <Table size="small" aria-label={`Serviços da unidade ${r.unidade}`}>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Serviço / tabela</TableCell>
                                                        <TableCell align="right">Quantidade</TableCell>
                                                        <TableCell align="right">Preço</TableCell>
                                                        <TableCell align="right">Total</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {r.itens.map((it) => (
                                                        <TableRow key={it.vinculoId}>
                                                            <TableCell>
                                                                {it.descricao}
                                                                <Typography variant="caption" display="block">
                                                                    {it.tabela || `Tabela ${it.tabelaPrecoId}`} ·{' '}
                                                                    {it.embalagem || `Embalagem ${it.embalagemId}`}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                {Number(it.quantidade).toLocaleString('pt-BR')}
                                                            </TableCell>
                                                            <TableCell align="right">{moeda(it.preco)}</TableCell>
                                                            <TableCell align="right">{moeda(it.total)}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    )}
                                    {r.cobranca && (
                                        <Alert severity={r.cobranca.pendente ? 'warning' : 'info'} sx={{ mt: 1 }}>
                                            Movimento {r.cobranca.movimentoId}.{' '}
                                            {r.cobranca.pendente
                                                ? 'Possui pendência registrada.'
                                                : 'Cobrança mensal já registrada.'}
                                            {r.cobranca.pendente && (
                                                <Button
                                                    disabled={executando}
                                                    onClick={() =>
                                                        setRegularizacao({
                                                            cobrancaId: r.cobranca.id,
                                                            dataVencimento: '',
                                                            motivo: '',
                                                        })
                                                    }
                                                >
                                                    Regularizar pendência
                                                </Button>
                                            )}
                                        </Alert>
                                    )}
                                    {r.status !== 'sem_servicos' && (
                                        <Typography
                                            sx={{ mt: 2, pt: 1.5, borderTop: 1, borderColor: 'divider', textAlign: 'right' }}
                                            fontWeight="bold"
                                        >
                                            Principal: {moeda(r.total)}
                                        </Typography>
                                    )}
                                    {r.status === 'sem_servicos' && (
                                        <Typography>Sem serviços vigentes para esta competência.</Typography>
                                    )}
                                    {r.criticas.map((c, i) => (
                                        <Alert
                                            key={`${c.codigo}-${i}`}
                                            severity={c.nivel === 'bloqueio' ? 'error' : 'warning'}
                                            sx={{ mt: 1 }}
                                        >
                                            {c.mensagem} — Corrigir em: {c.local}.
                                        </Alert>
                                    ))}
                                </Paper>
                            ))}
                            {!linhas.length && <Typography>Nenhuma unidade para os filtros selecionados.</Typography>}
                        </Stack>
                    </>
                )}
            </Box>
        </ThemeProvider>
    );
}
